import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useSearchQuery } from "../hooks/useSearch/useSearchQuery";

import { MyPageProductSearchForm } from "../layouts/mypage/MyPageProductSearchForm";
import { MyPageProductDataArea } from "../layouts/mypage/MyPageProductDataArea";
import ModalGreenBeanAdd from "../layouts/mypage/ModalGreenBeanAdd";
import ModalGreenBeanPlan from "../layouts/mypage/ModalGreenBeanPlan";

import ContentLayout from "../layouts/ContentLayout";
import { processingTypes } from "../constants/searchpageTexts";
import { isDebug } from "../utils";


const MyPage = () => {
  const access = sessionStorage.getItem("access");
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    CoffeeName: "", // 생두 이름
    BeanVariety: "", // 생두 품종
    ProductionCountryCode: "", // 원산지 코드
    ProductionYear: "", // 생산년도
    Farm: "", // 생산농장
    Altitude: "", // 고도
    ProcessingCode: "", // 가공방식
    StockDate: "", // 입고일자
  });
  const [searchPayload, setSearchPayload] = useState({});
  const [searchTriggered, setSearchTriggered] = useState(true);
  const [selectedOriginValue, setSelectedOriginValue] = useState({
    code: "",
    name: "",
  });
  const [modalType, setModalType] = useState(null);

  if (isDebug()) console.log("##@@@selectedOriginValue", selectedOriginValue);

  const openModal = (type, step_type) => {
    if (!access || access === "null") {
      navigate("/login", { replace: true });
      if (isDebug()) console.log("####@@@@loader", navigate);
      return;
    }
    setModalType(type);
  };

  const handleOriginSelect = (originData) => {
    if (isDebug()) console.log("##@@Received origin data:", originData);
    setSelectedOriginValue({
      code: originData.originCode,
      name: originData.originName,
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (isDebug()) console.log(`Input changed - ${name}: ${value}`);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (newValue) => {
    const date = dayjs(newValue);
    if (date.isValid()) {
      const formattedDate = date.format("YYYYMMDD");
      setFormData((prevFormData) => ({
        ...prevFormData,
        StockDate: formattedDate,
      }));
    } else {
      console.error("Invalid date provided");
    }
  };

  const handleYearChange = (newValue) => {
    const yearDate = newValue ? dayjs(newValue).startOf("year") : null;
    if (yearDate && yearDate.isValid()) {
      const yearAsNumber = yearDate.year();
      if (isDebug()) console.log("####Selected Year:", yearAsNumber);
      setFormData((prevFormData) => ({
        ...prevFormData,
        ProductionYear: yearAsNumber,
      }));
    } else {
      if (isDebug()) console.error("Invalid date provided");
      setFormData((prevFormData) => ({
        ...prevFormData,
        ProductionYear: null,
      }));
    }
  };

  const handleReset = () => {
    setFormData({
      CoffeeName: "",
      BeanVariety: "",
      ProductionCountryCode: "",
      ProductionYear: null,
      Farm: "",
      Altitude: "",
      ProcessingCode: "",
      StockDate: null,
    });
  };

  const handleSearch = () => {
    const formattedStockDate = dayjs(formData.StockDate).format("YYYYMMDD");
    const processingCodeKey = Object.keys(processingTypes).find(
      (key) => processingTypes[key] === formData.ProcessingCode
    );
    const filteredPayload = Object.entries(formData).reduce(
      (acc, [key, value]) => {
        if (value) {
          if (key === "ProcessingCode") {
            acc[key] = processingCodeKey || value;
          } else if (key === "StockDate") {
            acc[key] = formattedStockDate;
          } else {
            acc[key] = value;
          }
        }
        return acc;
      },
      {}
    );

    filteredPayload.ProductionCountryCode = selectedOriginValue.code;
    if (isDebug()) console.log("Selected Origin Code:", selectedOriginValue.code);
    setSearchPayload(filteredPayload);
    setSearchTriggered(true);
  };

  const {
    data: greenBeansData,
    isLoading,
    error,
  } = useSearchQuery(
    {
      keyword: searchPayload,
      pageType: "myPage",
    },
    {
      enabled: searchTriggered,
    }
  );

  if (isDebug()) console.log("[MyPage]", searchTriggered, greenBeansData);

  React.useEffect(() => {
    document.title = 'BWOB - ' + t('nav.mypage'); 
  }, []);


  return (
    <ContentLayout pageTitle={t('nav.mypage')} pageDescription={""} >
      &nbsp;

      <MyPageProductSearchForm
        formData={formData}
        selectedOrigin={selectedOriginValue}
        onFinalClickInfoChange={handleOriginSelect}
        onInputChange={handleInputChange}
        onDateChange={handleDateChange}
        onYearChange={handleYearChange}
        onSearch={handleSearch}
        processingTypes={processingTypes}
        greenBeansData={greenBeansData}
        onReset={handleReset}
        openModal={openModal}
        searchResultsCount={
          searchTriggered && greenBeansData?.length
            ? greenBeansData.length
            : "0"
        }
      />

      <MyPageProductDataArea
        greenBeansData={greenBeansData}
        formData={formData}
      />

      <>
        {modalType === "GreenBeanReg" && (
          <ModalGreenBeanAdd onClose={() => setModalType(null)} />
        )}
        {modalType === "PostRegSteps" && (
          <ModalGreenBeanPlan onClose={() => setModalType(null)} />
        )}
      </>

    </ContentLayout>
  );
};

export default MyPage;
